/**
 * useClassData Hook
 * 
 * Fetches rows/documents from a specific Parse class using REST API.
 * Supports pagination and returns count.
 * 
 * Requires: parseServerURL, appId, masterKey, and className
 */

import { useState, useEffect, useCallback } from 'react';
import { ParseRow, ParseClassDataResponse } from '../types/AppPanel';

interface UseClassDataParams {
  /** Parse Server URL (e.g., https://parseapi.back4app.com/parse) */
  parseServerURL?: string;
  /** Application ID */
  appId?: string;
  /** Master Key */
  masterKey?: string;
  /** Class name to fetch data from */
  className?: string;
  /** Number of rows to fetch (default: 100) */
  limit?: number;
  /** Number of rows to skip (for pagination) */
  skip?: number;
  /** Whether to include count in response */
  includeCount?: boolean;
  /** Field to order by (default: createdAt) */
  orderBy?: string;
  /** Order direction: 'asc' or 'desc' (default: desc) */
  orderDirection?: 'asc' | 'desc';
}

interface UseClassDataResult {
  /** Array of rows/documents */
  rows: ParseRow[];
  /** Total count of documents in the class */
  count: number;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch the data */
  refetch: () => void;
}

/**
 * Hook to fetch data from a Parse class
 * 
 * @param params - Query parameters
 * @returns Class data and loading state
 */
export function useClassData({
  parseServerURL,
  appId,
  masterKey,
  className,
  limit = 100,
  skip = 0,
  includeCount = true,
  orderBy = 'createdAt',
  orderDirection = 'desc',
}: UseClassDataParams): UseClassDataResult {
  const [rows, setRows] = useState<ParseRow[]>([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!parseServerURL || !appId || !masterKey || !className) {
      setRows([]);
      setCount(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Normalize URL
      const baseUrl = parseServerURL.replace(/\/+$/, '');
      
      // Build query params
      // Parse order format: "-field" for desc, "field" for asc
      const orderPrefix = orderDirection === 'asc' ? '' : '-';
      const params = new URLSearchParams({
        limit: String(limit),
        skip: String(skip),
        order: `${orderPrefix}${orderBy}`,
      });
      
      if (includeCount) {
        params.append('count', '1');
      }

      const response = await fetch(
        `${baseUrl}/classes/${className}?${params}`,
        {
          method: 'GET',
          headers: {
            'X-Parse-Application-Id': appId,
            'X-Parse-Master-Key': masterKey,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid credentials');
        }
        if (response.status === 404) {
          // Class doesn't exist - not necessarily an error
          setRows([]);
          setCount(0);
          return;
        }
        throw new Error(`Failed to fetch class data: ${response.status}`);
      }

      const data: ParseClassDataResponse = await response.json();
      setRows(data.results || []);
      setCount(data.count ?? data.results?.length ?? 0);
    } catch (err) {
      console.error('Error fetching class data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setRows([]);
      setCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [parseServerURL, appId, masterKey, className, limit, skip, includeCount, orderBy, orderDirection]);

  // Fetch on mount and when params change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    rows,
    count,
    isLoading,
    error,
    refetch: fetchData,
  };
}

export default useClassData;

